import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, Zap, IndianRupee, Activity, Clock, Server } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { maintAnalyticsApi as api } from "../../../api/maintenance/analytics";
import type {
  KeyMetrics, DailyTrendPoint, StatusBreakdown, UserSegment, PeakHourCell,
  TopStation, OrgRollup, PowerUser, VehicleUsage, OcppVersionUsage,
  UserGrowthPoint, DeviceOnboardingPoint, CohortRow, StationMapPoint, ActiveUser,
} from "../../../api/maintenance/analytics";
import UserDetailDrawer from "./UserDetailDrawer";
import "../shared/maintenance-analytics.scss";
import { useNotice } from "../shared/useNotice";

const PALETTE = ["#5554d3", "#2e7d32", "#e65100", "#1565c0", "#7b3ff2"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const validDay = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
// API speaks UTC only — convert the picked local (IST) day to a true UTC instant here.
const isoStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const isoEnd = (d: string) => new Date(`${d}T23:59:59`).toISOString();
const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(Math.round(v))}`);
const shortDate = (s: string) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });

type Metric = "sessions" | "energy_kwh" | "revenue";
const METRICS: { id: Metric; label: string; color: string; money?: boolean; d?: number }[] = [
  { id: "sessions", label: "Sessions", color: PALETTE[0] },
  { id: "energy_kwh", label: "Energy (kWh)", color: PALETTE[1], d: 1 },
  { id: "revenue", label: "Revenue (₹)", color: PALETTE[2], money: true },
];

type PresetId = "7" | "30" | "90" | "";
const PRESETS: { id: PresetId; label: string }[] = [
  { id: "7", label: "7d" }, { id: "30", label: "30d" }, { id: "90", label: "90d" },
];

const KPI: React.FC<{ icon: React.ReactNode; bg: string; label: string; value: React.ReactNode; sub: string }> =
  ({ icon, bg, label, value, sub }) => (
    <div className="ma-kpi">
      <div className="ma-kpi-ic" style={{ background: bg }}>{icon}</div>
      <div className="ma-kpi-label">{label}</div>
      <div className="ma-kpi-value">{value}</div>
      <div className="ma-kpi-sub">{sub}</div>
    </div>
  );

/** Horizontal top-N bars (css, single indigo hue). */
const Bars: React.FC<{ rows: { name: string; value: number }[]; fmt?: (v: number) => string; color?: string }> =
  ({ rows, fmt = (v) => num(v), color = PALETTE[0] }) => {
    const max = Math.max(1, ...rows.map((r) => r.value));
    if (!rows.length) return <p className="ma-empty">No data</p>;
    return (
      <div className="ma-bars">
        {rows.map((r, i) => (
          <div className="ma-bar" key={i}>
            <span className="nm" title={r.name}>{r.name}</span>
            <span className="track"><span className="fill" style={{ width: `${(r.value / max) * 100}%`, background: color }} /></span>
            <span className="val">{fmt(r.value)}</span>
          </div>
        ))}
      </div>
    );
  };

const Donut: React.FC<{ data: { name: string; value: number }[] }> = ({ data }) => {
  if (!data.length) return <p className="ma-empty">No data</p>;
  return (
    <>
      <ResponsiveContainer width="100%" height={210}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2} stroke="#fff" strokeWidth={2}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v: any) => num(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};

const OverviewDashboard: React.FC = () => {
  const today = new Date();
  const [start, setStart] = useState(ymd(new Date(today.getTime() - 30 * 864e5)));
  const [end, setEnd] = useState(ymd(today));
  const [preset, setPreset] = useState<PresetId>("30");
  const [metric, setMetric] = useState<Metric>("sessions");
  const [error, setError] = useNotice("error", "");
  const [loading, setLoading] = useState(true);

  const [km, setKm] = useState<KeyMetrics | null>(null);
  const [daily, setDaily] = useState<DailyTrendPoint[]>([]);
  const [status, setStatus] = useState<StatusBreakdown[]>([]);
  const [segments, setSegments] = useState<UserSegment[]>([]);
  const [peak, setPeak] = useState<PeakHourCell[]>([]);
  const [top, setTop] = useState<TopStation[]>([]);
  const [orgs, setOrgs] = useState<OrgRollup[]>([]);
  const [power, setPower] = useState<PowerUser[]>([]);
  const [vehicles, setVehicles] = useState<VehicleUsage[]>([]);
  const [ocpp, setOcpp] = useState<OcppVersionUsage[]>([]);
  const [growth, setGrowth] = useState<UserGrowthPoint[]>([]);
  const [onboarding, setOnboarding] = useState<DeviceOnboardingPoint[]>([]);
  const [cohort, setCohort] = useState<CohortRow[]>([]);
  const [stationMap, setStationMap] = useState<StationMapPoint[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [userId, setUserId] = useState<number | null>(null);

  const win = useCallback(() => ({ start: isoStart(start), end: isoEnd(end) }), [start, end]);

  const applyPreset = (id: PresetId) => {
    const now = new Date();
    const days = id === "7" ? 7 : id === "90" ? 90 : 30;
    setStart(ymd(new Date(now.getTime() - days * 864e5)));
    setEnd(ymd(now));
    setPreset(id);
  };

  // window-independent (fixed relative periods / all-time) — fetch once
  useEffect(() => {
    api.keyMetrics().then((r) => setKm(r.data)).catch(() => {});
    api.userSegments().then((r) => setSegments(r.data)).catch(() => {});
    api.deviceOnboarding().then((r) => setOnboarding(r.data)).catch(() => {});
    api.cohortRetention().then((r) => setCohort(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!validDay(start) || !validDay(end)) return; // don't fetch on a half-typed date
    setLoading(true);
    setError("");
    const p = win();
    try {
      const [d, s, pk, tp, og, pw, vh, oc, gr, sm, au] = await Promise.all([
        api.dailyTrend(p), api.sessionStatus(p), api.peakHours(p), api.topStations({ ...p, limit: 10 }),
        api.orgs(p), api.powerUsers(p), api.vehicleUsage(p), api.ocppVersions(p), api.userGrowth(p),
        api.stationMap(p), api.activeUsers(p),
      ]);
      setDaily(d.data); setStatus(s.data); setPeak(pk.data); setTop(tp.data);
      setOrgs(og.data); setPower(pw.data); setVehicles(vh.data); setOcpp(oc.data); setGrowth(gr.data);
      setStationMap(sm.data); setActiveUsers(au.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [win, start, end]);

  useEffect(() => { load(); }, [load]);

  const mMeta = METRICS.find((m) => m.id === metric)!;
  const peakMax = useMemo(() => Math.max(1, ...peak.map((c) => c.sessions)), [peak]);
  const peakGrid = useMemo(() => {
    const g: Record<string, number> = {};
    peak.forEach((c) => { g[`${c.day_num}-${c.hour}`] = c.sessions; });
    return g;
  }, [peak]);

  const statusData = status.map((s) => ({ name: s.status, value: s.sessions }));
  const segData = segments.map((s) => ({ name: s.user_segment.replace(/^\d+\.\s*/, ""), value: s.user_count }));

  return (
    <MaintenanceLayout title="Analytics Overview (Maintenance)">
      <div className="ma">
        <div className="ma-toolbar">
          <div className="ma-presets">
            {PRESETS.map((p) => (
              <button key={p.id} className={`ma-preset ${preset === p.id ? "on" : ""}`} onClick={() => applyPreset(p.id)}>{p.label}</button>
            ))}
          </div>
          <label className="ma-date">From<input type="date" value={start} max={end} onChange={(e) => { setStart(e.target.value); setPreset(""); }} /></label>
          <label className="ma-date">To<input type="date" value={end} min={start} onChange={(e) => { setEnd(e.target.value); setPreset(""); }} /></label>
        </div>

        {error && <div className="ma-error">{error}</div>}
        <div className="ma-note">
          Platform-wide (all orgs), matching the old Redash “EV Charging Analytics”. Revenue here is <strong>theoretical</strong>
          {" "}(energy × rate); actual charged revenue lives on the Transactions page. KPI tiles are fixed periods (MTD / last 45d).
        </div>

        {/* KPI strip — fixed relative periods, not affected by the window above */}
        <div className="ma-kpis">
          <KPI label="Active users" value={km ? num(km.active_users_45d) : "—"} sub="last 45 days" bg="#eef0ff" icon={<Users size={14} color={PALETTE[0]} />} />
          <KPI label="Sessions" value={km ? num(km.sessions_this_month) : "—"} sub="this month" bg="#fff3e0" icon={<Activity size={14} color={PALETTE[2]} />} />
          <KPI label="Energy" value={km ? num(km.energy_this_month_kwh, 0) : "—"} sub="kWh this month" bg="#e6f6ea" icon={<Zap size={14} color={PALETTE[1]} />} />
          <KPI label="Revenue" value={km ? money(km.revenue_this_month) : "—"} sub="this month (theoretical)" bg="#eef0ff" icon={<IndianRupee size={14} color={PALETTE[0]} />} />
          <KPI label="Avg duration" value={km ? `${num(km.avg_duration_mins, 0)}m` : "—"} sub="last 30 days" bg="#eaf1fb" icon={<Clock size={14} color={PALETTE[3]} />} />
          <KPI label="Stations" value={km ? num(km.active_stations) : "—"} sub={km ? `${km.total_stations} total · ${km.inactive_stations} inactive` : ""} bg="#f0f0f0" icon={<Server size={14} color="#888" />} />
        </div>

        {loading ? <p className="ma-loading">Loading analytics…</p> : (
          <div className="ma-grid">
            {/* daily activity — single-metric area (no dual axis) */}
            <div className="ma-card two-third">
              <div className="ma-card-h">
                <h3>Daily activity <span className="ma-hint">· {start} → {end}</span></h3>
                <div className="ma-toggles">
                  {METRICS.map((m) => (
                    <button key={m.id} className={`ma-tg ${metric === m.id ? "on" : ""}`} onClick={() => setMetric(m.id)}>{m.label.split(" ")[0]}</button>
                  ))}
                </div>
              </div>
              <div className="ma-card-b">
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={daily} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={mMeta.color} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={mMeta.color} stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDate} stroke="#8a92a1" fontSize={11} minTickGap={24} />
                    <YAxis stroke="#8a92a1" fontSize={11} width={44} tickFormatter={(v) => mMeta.money ? `₹${num(v)}` : num(v)} />
                    <Tooltip labelFormatter={(l) => shortDate(String(l))} formatter={(v: any) => [mMeta.money ? money(v) : num(v, mMeta.d ?? 0), mMeta.label]} />
                    <Area type="monotone" dataKey={metric} stroke={mMeta.color} strokeWidth={2} fill="url(#gArea)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* session status */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>Session status</h3></div>
              <div className="ma-card-b"><Donut data={statusData} /></div>
            </div>

            {/* peak hours heatmap */}
            <div className="ma-card two-third">
              <div className="ma-card-h"><h3>Peak hours <span className="ma-hint">· sessions by day × hour (IST)</span></h3></div>
              <div className="ma-card-b">
                <div className="ma-heat">
                  <span />
                  {Array.from({ length: 24 }).map((_, h) => <span key={h} className="ma-heat-h">{h % 3 === 0 ? h : ""}</span>)}
                  {DOW.map((d, di) => (
                    <React.Fragment key={di}>
                      <span className="ma-heat-rl">{d}</span>
                      {Array.from({ length: 24 }).map((_, h) => {
                        const v = peakGrid[`${di}-${h}`] || 0;
                        const a = v === 0 ? 0 : 0.12 + (v / peakMax) * 0.88;
                        return <span key={h} className="ma-heat-c" title={`${d} ${h}:00 — ${v} sessions`} style={{ background: v === 0 ? "#f1f2f6" : `rgba(85,84,211,${a.toFixed(2)})` }} />;
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* user segments */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>User segments <span className="ma-hint">· by recency</span></h3></div>
              <div className="ma-card-b"><Bars rows={segData.map((s) => ({ name: s.name, value: s.value }))} /></div>
            </div>

            {/* top stations */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>Top stations <span className="ma-hint">· sessions</span></h3></div>
              <div className="ma-card-b"><Bars rows={top.map((t) => ({ name: t.station, value: t.sessions }))} /></div>
            </div>

            {/* revenue by org */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>Revenue by org <span className="ma-hint">· theoretical</span></h3></div>
              <div className="ma-card-b"><Bars rows={[...orgs].sort((a, b) => b.revenue - a.revenue).slice(0, 8).map((o) => ({ name: o.organization, value: o.revenue }))} fmt={money} color={PALETTE[1]} /></div>
            </div>

            {/* vehicles + ocpp */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>Vehicle usage <span className="ma-hint">· sessions</span></h3></div>
              <div className="ma-card-b"><Bars rows={vehicles.map((v) => ({ name: v.vehicle, value: v.sessions }))} color={PALETTE[4]} /></div>
            </div>

            {/* user growth (cumulative) */}
            <div className="ma-card two-third">
              <div className="ma-card-h"><h3>User growth <span className="ma-hint">· cumulative first-charge</span></h3></div>
              <div className="ma-card-b">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={growth} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gGrowth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={PALETTE[3]} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={PALETTE[3]} stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDate} stroke="#8a92a1" fontSize={11} minTickGap={24} />
                    <YAxis stroke="#8a92a1" fontSize={11} width={44} />
                    <Tooltip labelFormatter={(l) => shortDate(String(l))} formatter={(v: any) => [num(v), "Users"]} />
                    <Area type="monotone" dataKey="cumulative_users" stroke={PALETTE[3]} strokeWidth={2} fill="url(#gGrowth)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ocpp versions */}
            <div className="ma-card third">
              <div className="ma-card-h"><h3>OCPP versions</h3></div>
              <div className="ma-card-b"><Bars rows={ocpp.map((o) => ({ name: o.ocpp_version, value: o.sessions }))} color={PALETTE[3]} /></div>
            </div>

            {/* device onboarding (cumulative, all-time) */}
            <div className="ma-card two-third">
              <div className="ma-card-h"><h3>Device onboarding <span className="ma-hint">· cumulative, all-time</span></h3></div>
              <div className="ma-card-b">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={onboarding} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gOnb" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={PALETTE[4]} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={PALETTE[4]} stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDate} stroke="#8a92a1" fontSize={11} minTickGap={30} />
                    <YAxis stroke="#8a92a1" fontSize={11} width={44} />
                    <Tooltip labelFormatter={(l) => shortDate(String(l))} formatter={(v: any) => [num(v), "Devices"]} />
                    <Area type="monotone" dataKey="cumulative_devices" stroke={PALETTE[4]} strokeWidth={2} fill="url(#gOnb)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* org rollup table */}
            <div className="ma-card half">
              <div className="ma-card-h"><h3>Organizations <span className="ma-hint">· window</span></h3></div>
              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>Org</th><th className="num">Sessions</th><th className="num">Users</th><th className="num">Energy</th><th className="num">Revenue</th></tr></thead>
                  <tbody>
                    {orgs.slice(0, 12).map((o) => (
                      <tr key={o.org_id}>
                        <td>{o.organization}<div className="sub">{o.stations} stn · {o.active_users} active</div></td>
                        <td className="num">{num(o.sessions)}</td>
                        <td className="num">{num(o.active_users_window)}</td>
                        <td className="num">{num(o.energy_kwh, 1)}</td>
                        <td className="num">{money(o.revenue)}</td>
                      </tr>
                    ))}
                    {!orgs.length && <tr><td colSpan={5} className="ma-empty">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* power users table */}
            <div className="ma-card half">
              <div className="ma-card-h"><h3>Power users <span className="ma-hint">· top 20 by sessions</span></h3></div>
              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>User</th><th className="num">Sessions</th><th className="num">kWh</th><th className="num">Spent</th></tr></thead>
                  <tbody>
                    {power.map((u) => (
                      <tr key={u.user_id} className="clickable" onClick={() => setUserId(u.user_id)}>
                        <td>{u.display_name || u.phone || `#${u.user_id}`}<div className="sub">{u.organization || ""}</div></td>
                        <td className="num">{num(u.total_sessions)}</td>
                        <td className="num">{num(u.total_kwh, 1)}</td>
                        <td className="num">{money(u.total_spent)}</td>
                      </tr>
                    ))}
                    {!power.length && <tr><td colSpan={4} className="ma-empty">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* cohort retention */}
            <div className="ma-card">
              <div className="ma-card-h"><h3>Monthly cohort retention <span className="ma-hint">· % of cohort active N months later</span></h3></div>
              <div className="ma-tablewrap ma-card-b">
                <table className="ma-table">
                  <thead><tr><th>Cohort</th><th className="num">Size</th>{[0, 1, 2, 3, 4, 5].map((m) => <th key={m} className="num">M{m}</th>)}</tr></thead>
                  <tbody>
                    {cohort.map((c) => {
                      const cells = [c.month_0, c.month_1, c.month_2, c.month_3, c.month_4, c.month_5];
                      return (
                        <tr key={c.cohort}>
                          <td>{c.cohort}</td>
                          <td className="num">{num(c.cohort_size)}</td>
                          {cells.map((v, i) => {
                            const p = c.cohort_size ? v / c.cohort_size : 0;
                            return <td key={i} className="num" style={{ background: v ? `rgba(46,125,50,${(0.1 + p * 0.8).toFixed(2)})` : undefined, color: p > 0.5 ? "#fff" : undefined }}>{v ? `${Math.round(p * 100)}%` : "—"}</td>;
                          })}
                        </tr>
                      );
                    })}
                    {!cohort.length && <tr><td colSpan={8} className="ma-empty">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* active users list */}
            <div className="ma-card half">
              <div className="ma-card-h"><h3>Active users <span className="ma-hint">· last 45 days (top 50)</span></h3></div>
              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>User</th><th>Org</th><th className="num">Sessions</th><th>Last session</th></tr></thead>
                  <tbody>
                    {activeUsers.slice(0, 50).map((u) => (
                      <tr key={u.user_id} className="clickable" onClick={() => setUserId(u.user_id)}>
                        <td>{u.display_name || u.phone || `#${u.user_id}`}<div className="sub">{u.phone || ""}</div></td>
                        <td className="sub">{u.organization || "—"}</td>
                        <td className="num">{num(u.sessions)}</td>
                        <td className="sub">{u.last_session ? shortDate(u.last_session) : "—"}</td>
                      </tr>
                    ))}
                    {!activeUsers.length && <tr><td colSpan={4} className="ma-empty">No active users</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* station map — table (coordinates; no map library yet) */}
            <div className="ma-card half">
              <div className="ma-card-h"><h3>Stations <span className="ma-hint">· window activity + location</span></h3></div>
              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>Station</th><th>Org</th><th className="num">Sessions</th><th className="num">kWh</th><th>Status</th></tr></thead>
                  <tbody>
                    {stationMap.map((s) => (
                      <tr key={s.station_guid || s.station}>
                        <td>{s.station}<div className="sub">{s.lat != null && s.lon != null ? `${s.lat.toFixed(4)}, ${s.lon.toFixed(4)}` : "no coords"}</div></td>
                        <td className="sub">{s.organization || "—"}</td>
                        <td className="num">{num(s.sessions_30d)}</td>
                        <td className="num">{num(s.kwh_30d, 1)}</td>
                        <td><span className={`ma-pill ${s.status === "active" ? "ok" : "muted"}`}>{s.status}</span></td>
                      </tr>
                    ))}
                    {!stationMap.length && <tr><td colSpan={5} className="ma-empty">No stations</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <UserDetailDrawer userId={userId} onClose={() => setUserId(null)} />
    </MaintenanceLayout>
  );
};

export default OverviewDashboard;
